import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Typography,
  makeStyles, Box, Accordion, AccordionSummary,
  AccordionDetails
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';


const useStyles = makeStyles(theme => ({
  dialogPaper: {
    padding: theme.spacing(2),
    maxWidth: '90%',
    margin: 'auto'
  },
  warningText: {
    marginBottom: theme.spacing(2)
  },
  actionButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5)
  },
  instructionStep: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1)
  },
  stepNumber: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: '50%',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(1),
    flexShrink: 0
  },
  stepText: {
    flex: 1
  },
  instructionsBox: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  accordion: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: 'none',
    '&:before': {
      display: 'none',
    },
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius + 'px !important',
    overflow: 'hidden'
  },
  accordionSummary: {
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: '48px !important',
    '&.Mui-expanded': {
      minHeight: '48px !important',
    }
  },
  accordionDetails: {
    display: 'block',
    padding: theme.spacing(2),
    textAlign: 'center'
  },
  gifContainer: {
    maxWidth: '100%',
    textAlign: 'center',
    margin: 0
  },
  instructionGif: {
    width: '100%',
    maxWidth: 280,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
  },
  fallbackText: {
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
    marginTop: theme.spacing(1)
  }
}));

interface TelegramWebViewDetectorProps {
  translate: (key: string, data?: any) => string;
  instructionGifUrl?: string;
}

const TelegramWebViewDetector: React.FC<TelegramWebViewDetectorProps> = ({
                                                                           translate,
                                                                           instructionGifUrl
                                                                         }) => {
  const classes = useStyles();
  const [isTelegramWebView, setIsTelegramWebView] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [gifLoaded, setGifLoaded] = useState<boolean>(true);

  const handleAccordionChange = (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  useEffect(() => {
    const detectTelegramWebView = () => {
      if (navigator.userAgent.includes("Android") && typeof (window as any).TelegramWebview !== 'undefined') {
        setIsTelegramWebView(true);
        return 'Android';
      }

      if (navigator.userAgent.includes("iPhone") &&
        typeof (window as any).TelegramWebviewProxy !== 'undefined' &&
        typeof (window as any).TelegramWebviewProxyProto !== 'undefined') {
        setIsTelegramWebView(true);
        return 'iOS';
      }

      return false;
    };

    const platform = detectTelegramWebView();

    if (platform) {
      const info = {
        platform: platform,
        userAgent: navigator.userAgent,
        telegramObjects: {
          Android: platform === 'Android' ? Object.keys((window as any).TelegramWebview || {}) : [],
          iOS: platform === 'iOS' ? {
            proxy: Object.keys((window as any).TelegramWebviewProxy || {}),
            proto: Object.keys((window as any).TelegramWebviewProxyProto || {})
          } : {}
        }
      };
    } else {
      console.log('Not running in Telegram WebView');
    }
  }, []);

  const handleGifError = () => {
    console.error('Error loading GIF image');
    setGifLoaded(false);
  };

  if (!isTelegramWebView) {
    return null;
  }

  return (
    <Dialog
      open={isTelegramWebView}
      classes={{ paper: classes.dialogPaper }}
      disableBackdropClick
      disableEscapeKeyDown
    >
      <DialogTitle>
        {translate('telegramWebView.title', 'Attention!')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" className={classes.warningText}>
          {translate('telegramWebView.message')}
        </Typography>

        <Box className={classes.instructionsBox}>
          <Typography variant="subtitle1" gutterBottom>
            {translate('telegramWebView.instructionTitle')}
          </Typography>

          <div className={classes.instructionStep}>
            <div className={classes.stepNumber}>1</div>
            <Typography className={classes.stepText}>
              {translate('telegramWebView.step1')}
            </Typography>
          </div>

          <div className={classes.instructionStep}>
            <div className={classes.stepNumber}>2</div>
            <Typography className={classes.stepText}>
              {translate('telegramWebView.step2')}
            </Typography>
          </div>

          <div className={classes.instructionStep}>
            <div className={classes.stepNumber}>3</div>
            <Typography className={classes.stepText}>
              {translate('telegramWebView.step3')}
            </Typography>
          </div>
        </Box>

        <Accordion
          expanded={expanded}
          onChange={handleAccordionChange}
          className={classes.accordion}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
          >
            <Typography>
              {translate('telegramWebView.visualInstruction')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            {instructionGifUrl && (
              <div className={classes.gifContainer}>
                <img
                  src={instructionGifUrl}
                  alt={translate('telegramWebView.gifAlt')}
                  className={classes.instructionGif}
                  onError={handleGifError}
                />
              </div>
            )}

            {(!instructionGifUrl || !gifLoaded) && (
              <Typography variant="body2" className={classes.fallbackText}>
                {translate('telegramWebView.gifFallback')}
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>

        <Typography variant="body2">
          {translate('telegramWebView.benefits')}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default TelegramWebViewDetector;
